import {z} from 'zod';
import {ChatItemPartType, RagSearchResponseAnswerType} from './copilot.enums';

export const plaintextPartSchema = z
  .object({
    type: z.literal(ChatItemPartType.Plaintext),
    content: z.string(),
  })
  .describe('content part for plain text');

export const isoDateUtcSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/, 'Invalid ISO 8601 UTC date format');

export const entityToRenameSchema = z.object({
  id: z.string(),
  currentName: z.string(),
  newName: z.string().min(1, 'Name cannot be empty').nullable(),
});

export const routeResponseSchema = z.object({
  isInTheMiddleOfMultiTurnTask: z
    .boolean()
    .describe(
      'Whether the current agent is in the middle of a multi-turn task (e.g., asking questions, gathering information). When true, lean towards staying with the current agent.',
    ),
  canCurrentAgentHandleRequestEffectively: z
    .boolean()
    .describe(
      'Whether the current agent can handle the request effectively within their scope. When true, lean towards staying with the current agent.',
    ),
  wouldDifferentAgentProvideSignificantlyBetterResults: z
    .boolean()
    .describe(
      'Whether a different agent would provide significantly better results. When true, lean towards handoff to a different agent.',
    ),
  isValuableConversationContextLostInHandoff: z
    .boolean()
    .describe(
      'Whether there is valuable conversation context that would be lost in a handoff. When true, lean towards staying with the current agent.',
    ),
  continueWithCurrentAgentReason: z
    .string()
    .optional()
    .nullable()
    .describe(
      "Why you recommend staying with the current agent, based on the above criteria. Max 15 words, user-friendly. Should be null if you don't recommend staying with the current agent.",
    ),
  handoffReason: z
    .string()
    .optional()
    .nullable()
    .describe(
      "Why you recommend handoff to a different agent, based on the above criteria. Max 15 words, user-friendly. Should be null if you don't recommend handoff to a different agent.",
    ),

  isHandoffRequired: z.boolean().describe('Whether a handoff from the current agent is needed.'),
  targetAgentName: z
    .string()
    .describe('Agent name to handle this message. Current agent if staying, new agent if handoff.'),
});

export const factSchema = z.object({
  what: z.string().describe('What happened - the user request or action'),
  result: z.string().describe('What was accomplished - the outcome or result'),
  entities: z
    .array(z.string())
    .describe(
      "Concrete values, names, or data points mentioned (e.g., city names, numbers, file names). Exclude generic terms like 'user' or 'assistant'.",
    ),
});

export const sessionSummarySchema = z.object({
  facts: z.array(factSchema).describe('Key facts from the conversation'),
});

export const generalKnowledgeToolSchema = z.object({
  prompt: z
    .string()
    .describe(
      "The user's question or prompt. This may consist of not only the last user prompt, but what was spoken of in the conversation before it.",
    ),
});

export const ragSearchResponseSchema = z.object({
  answer: z
    .string()
    .describe(
      "The answer to the user's question, even if it's incomplete or a request for clarification",
    ),
  answerType: z.nativeEnum(RagSearchResponseAnswerType),
});

export const askKnowledgeRAGToolSchema = z.object({
  prompt: z.string().describe("The user's question or prompt to search for in the knowledge base."),
  context: z
    .string()
    .describe(
      'Additional background, details, or relevant items (such as prior conversation facts) that can help provide an accurate answer.',
    ),
});

export const routeResponseSlimSchema = z.object({
  targetAgentName: z.string().describe('Agent name to handle this message.'),
  reason: z.string().describe('Brief reason for this routing decision. Max 10 words.'),
});
